import { Transforms, Editor, Range, Point } from 'slate';

/**
 * Plugin for link handling: prevents splitting links on edit, handles URL pasting
 * @param {Editor} editor - Slate editor instance
 * @returns {Editor} Extended editor with link handling
 */
const withLinks = (editor) => {
    const { isInline, insertData, insertText, insertBreak } = editor;

    // Ensure links are treated as inline elements
    editor.isInline = (element) => (element.type === 'link' ? true : isInline(element));

    // Handle pasting URLs
    editor.insertData = (data) => {
        const text = (data.getData('text/plain') || '').trim(); // Get the plain text from the clipboard
        const isUrl = isValidUrl(text); // Check if the pasted text is a valid URL

        if (isUrl) {
            wrapLink(editor, text); // Wrap the URL in a link node
        } else {
            insertData(data); // Default behavior for non-URL text
        }
    };

    // Handle typing after a link
    editor.insertText = (text) => {
        if (isLinkActive(editor) && isSelectionAtEndOfLink(editor)) {
            // Exit the link before inserting text
            Transforms.move(editor, { distance: 1, unit: 'offset' });
        }
        insertText(text);
    };

    // Handle pressing "Enter" after a link
    editor.insertBreak = () => {
        if (isLinkActive(editor) && isSelectionAtEndOfLink(editor)) {
            // Exit the link before inserting a new line
            Transforms.move(editor, { distance: 1, unit: 'offset' });
        }
        insertBreak();
    };

    return editor;
};

// Check if the current selection is inside a link
const isLinkActive = (editor) => {
    const [link] = Editor.nodes(editor, {
        match: (n) => n.type === 'link',
    });
    return !!link;
};

// Check if the selection is at the end of a link
const isSelectionAtEndOfLink = (editor) => {
    const { selection } = editor;
    if (!selection || !Range.isCollapsed(selection)) {
        return false;
    }

    const [link] = Editor.nodes(editor, {
        match: (n) => n.type === 'link',
    });

    if (!link) {
        return false;
    }

    const [, linkPath] = link;
    const end = Editor.end(editor, linkPath);

    return Point.equals(selection.anchor, end);
};

// Wrap the selected text or insert a new link
const wrapLink = (editor, url) => {
    if (isLinkActive(editor)) {
        unwrapLink(editor); // Unwrap the existing link if one is active
    }

    const { selection } = editor;
    const isCollapsed = selection && Range.isCollapsed(selection);

    const link = {
        type: 'link',
        href: url,
        children: [{ text: url }],
    };

    if (isCollapsed) {
        // Insert the link as a new inline node
        Transforms.insertNodes(editor, link);
    } else {
        // Wrap the selected text in a link
        Transforms.wrapNodes(editor, link, { split: true });
        Transforms.collapse(editor, { edge: 'end' });
    }
};

// Unwrap the link node
const unwrapLink = (editor) => {
    Transforms.unwrapNodes(editor, {
        match: (n) => n.type === 'link',
    });
};

// Validate if a string is a valid URL
const isValidUrl = (url) => {
    // More strict regex that requires a valid TLD and won't match simple numbers
    try {
        // Reject strings that are purely numeric with a decimal point
        if (/^\d+\.\d+$/.test(url)) {
            return false;
        }

        // Add protocol if missing for URL constructor
        const normalizedUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;

        const basicUrlPattern = /^(https?:\/\/)?(www\.)?([a-zA-Z0-9][-a-zA-Z0-9]*\.)+[a-zA-Z]{2,}(:[0-9]+)?(\/[^\s]*)?$/i;

        if (!basicUrlPattern.test(normalizedUrl)) {
            return false;
        }
        // Use the URL constructor for final validation
        // This handles parsing of all URL components including query parameters
        new URL(normalizedUrl);
        return true;
    } catch (e) {
        return false;
    }
};

export default withLinks;
